import { Controller } from "@hotwired/stimulus"
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'

// IMPORTANT: Password validation rules are synchronized across multiple files.
// If you update password requirements, you MUST also update:
// 1. config/initializers/devise.rb (password_length configuration)
// 2. app/models/validators/password_validator.rb (all validation methods)
// 3. This file (app/javascript/controllers/forms/password_validation_controller.js)

/**
 * Real-time password validation controller that matches backend validation rules.
 * 
 * Backend validation is defined in:
 * - config/initializers/devise.rb: password_length = 10..128
 * - app/models/validators/password_validator.rb: MIN_PASSWORD_SCORE = 3
 * 
 * Validation Rules:
 * 1. Length: 10-128 characters
 * 2. Complexity: zxcvbn score >= 3 (out of 4)
 * 3. Character set: At least one number AND one letter
 * 4. Weak words: Cannot contain email or 'argus'
 * 5. Passwords match: Password and confirmation must match
 * 
 * Targets:
 * - passwordField: The password input field
 * - passwordConfirmationField: The password confirmation input field
 * - emailField: (optional) The email field for weak word checking
 * - checklistContainer: Container for validation checklist
 * - submitButton: (optional) Form submit button to disable/enable
 * 
 * Usage:
 * <div data-controller="forms--password-validation">
 *   <input type="email" data-forms--password-validation-target="emailField" />
 *   <input type="password" data-forms--password-validation-target="passwordField" />
 *   <input type="password" data-forms--password-validation-target="passwordConfirmationField" />
 *   <div data-forms--password-validation-target="checklistContainer"></div>
 *   <button type="submit" data-forms--password-validation-target="submitButton">Submit</button>
 * </div>
 */
export default class extends Controller {
  static targets = ["passwordField", "passwordConfirmationField", "emailField", "checklistContainer", "submitButton"]

  // Backend constants from app/models/validators/password_validator.rb
  static MIN_PASSWORD_SCORE = 3
  static MIN_LENGTH = 10  // From config/initializers/devise.rb
  static MAX_LENGTH = 128 // From config/initializers/devise.rb
  static WEAK_WORDS = ['argus'] // From password_validator.rb

  connect() {
    // Initialize zxcvbn with English language support
    const options = {
      translations: zxcvbnEnPackage.translations,
      graphs: zxcvbnCommonPackage.adjacencyGraphs,
      dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEnPackage.dictionary,
      },
    }
    zxcvbnOptions.setOptions(options)

    // Initialize the checklist UI
    this.renderChecklist()
    
    // Validate on initial load if password field has value
    if (this.hasPasswordFieldTarget && this.passwordFieldTarget.value) {
      this.validatePassword()
    }
  }

  /**
   * Renders the validation checklist UI
   */
  renderChecklist() {
    if (!this.hasChecklistContainerTarget) return

    this.checklistContainerTarget.innerHTML = `
      <div class="password-validation-checklist mt-3">
        <div class="small text-muted mb-2">Password Requirements:</div>
        <ul class="list-unstyled small">
          <li data-rule="length">
            <span class="validation-icon">&nbsp;</span>
            <span class="validation-text">10-128 characters</span>
          </li>
          <li data-rule="hasNumber">
            <span class="validation-icon">&nbsp;</span>
            <span class="validation-text">At least one number</span>
          </li>
          <li data-rule="hasLetter">
            <span class="validation-icon">&nbsp;</span>
            <span class="validation-text">At least one letter</span>
          </li>
          <li data-rule="complexity">
            <span class="validation-icon">&nbsp;</span>
            <span class="validation-text">Strong enough (no common words or patterns)</span>
          </li>
          <li data-rule="weakWords">
            <span class="validation-icon">&nbsp;</span>
            <span class="validation-text">Doesn't contain email or 'argus'</span>
          </li>
          <li data-rule="passwordsMatch">
            <span class="validation-icon">&nbsp;</span>
            <span class="validation-text">Passwords match</span>
          </li>
        </ul>
        <div class="password-strength-feedback mt-2 small text-muted" data-feedback></div>
      </div>
    `
  }

  /**
   * Main validation method triggered on password input
   */
  validatePassword() {
    const password = this.passwordFieldTarget.value
    const passwordConfirmation = this.hasPasswordConfirmationFieldTarget ? this.passwordConfirmationFieldTarget.value : ''
    const email = this.hasEmailFieldTarget ? this.emailFieldTarget.value : ''

    // If password is empty, reset all indicators
    if (!password) {
      this.resetValidation()
      return
    }

    // Run all validation checks
    const results = {
      length: this.checkLength(password),
      hasNumber: this.checkHasNumber(password),
      hasLetter: this.checkHasLetter(password),
      complexity: this.checkComplexity(password, email),
      weakWords: this.checkWeakWords(password, email),
      passwordsMatch: this.checkPasswordsMatch(password, passwordConfirmation)
    }

    // Update UI for each rule
    this.updateChecklistUI(results)

    // Enable/disable submit button based on all rules passing
    const allValid = Object.values(results).every(result => result.valid)
    this.updateSubmitButton(allValid)

    return allValid
  }

  /**
   * Validates password length (10-128 characters)
   * Matches: validate_password_length in password_validator.rb
   */
  checkLength(password) {
    const valid = password.length >= this.constructor.MIN_LENGTH && 
                  password.length <= this.constructor.MAX_LENGTH
    let message = ''
    
    if (password.length < this.constructor.MIN_LENGTH) {
      message = `Too short (${password.length}/${this.constructor.MIN_LENGTH})`
    } else if (password.length > this.constructor.MAX_LENGTH) {
      message = `Too long (${password.length}/${this.constructor.MAX_LENGTH})`
    }

    return { valid, message }
  }

  /**
   * Validates password contains at least one number
   * Matches: validate_password_character_set in password_validator.rb
   */
  checkHasNumber(password) {
    const valid = /\d/.test(password)
    return { valid, message: valid ? '' : 'Add at least one number' }
  }

  /**
   * Validates password contains at least one letter
   * Matches: validate_password_character_set in password_validator.rb
   */
  checkHasLetter(password) {
    const valid = /\p{L}/u.test(password)
    return { valid, message: valid ? '' : 'Add at least one letter' }
  }

  /**
   * Validates password complexity using zxcvbn
   * Matches: validate_password_complexity in password_validator.rb
   */
  checkComplexity(password, email) {
    // Build weak words list matching backend logic
    const weakWords = [...this.constructor.WEAK_WORDS]
    
    if (email) {
      weakWords.push(email)
      // Split email into parts (matches DeviseZxcvbn::EmailTokeniser.split)
      const emailParts = email.split(/[@._-]/)
      weakWords.push(...emailParts)
    }

    // Run zxcvbn analysis
    const result = zxcvbn(password, weakWords)
    const valid = result.score >= this.constructor.MIN_PASSWORD_SCORE

    // Build feedback message matching backend format
    let message = ''
    if (!valid && result.feedback) {
      const feedbackParts = []
      if (result.feedback.warning) {
        feedbackParts.push(result.feedback.warning)
      }
      if (result.feedback.suggestions && result.feedback.suggestions.length > 0) {
        feedbackParts.push(...result.feedback.suggestions)
      }
      message = feedbackParts.join('. ').replace(/\.\s*\./g, '.')
      
      // Default message if library didn't provide feedback
      if (!message) {
        message = 'Add another word or two. Uncommon words are better.'
      }
    }

    return { valid, message, score: result.score }
  }

  /**
   * Validates password doesn't contain weak words (email or 'argus')
   * Part of: validate_password_complexity in password_validator.rb
   */
  checkWeakWords(password, email) {
    const lowerPassword = password.toLowerCase()
    
    // Check for 'argus'
    if (lowerPassword.includes('argus')) {
      return { valid: false, message: "Cannot contain 'argus'" }
    }

    // Check for email or email parts
    if (email) {
      const lowerEmail = email.toLowerCase()
      const emailParts = lowerEmail.split(/[@._-]/)
      
      for (const part of [lowerEmail, ...emailParts]) {
        if (part.length > 2 && lowerPassword.includes(part)) {
          return { valid: false, message: 'Cannot contain parts of your email' }
        }
      }
    }

    return { valid: true, message: '' }
  }

  /**
   * Validates that password and password confirmation match
   */
  checkPasswordsMatch(password, passwordConfirmation) {
    // If confirmation field doesn't exist or is empty, consider it valid (not yet filled)
    if (!this.hasPasswordConfirmationFieldTarget || !passwordConfirmation) {
      return { valid: true, message: '' }
    }

    const valid = password === passwordConfirmation
    return { valid, message: valid ? '' : 'Passwords do not match' }
  }

  /**
   * Updates the checklist UI with validation results
   */
  updateChecklistUI(results) {
    if (!this.hasChecklistContainerTarget) return

    const container = this.checklistContainerTarget
    
    // Update each rule indicator
    Object.entries(results).forEach(([rule, result]) => {
      const ruleElement = container.querySelector(`[data-rule="${rule}"]`)
      if (!ruleElement) return

      const icon = ruleElement.querySelector('.validation-icon')
      const text = ruleElement.querySelector('.validation-text')

      if (result.valid) {
        icon.textContent = '✓'
        icon.style.color = '#28a745'
        ruleElement.style.color = ''
      } else {
        icon.textContent = '✗'
        icon.style.color = '#dc3545'
        ruleElement.style.color = ''
        
        // Update text with specific message if available
        if (result.message) {
          const baseText = text.textContent.split(' - ')[0]
          text.textContent = baseText + ' - ' + result.message
        }
      }
    })

    // Update feedback section with complexity feedback
    const feedbackElement = container.querySelector('[data-feedback]')
    if (feedbackElement && results.complexity.message) {
      feedbackElement.textContent = results.complexity.message
      feedbackElement.style.color = results.complexity.valid ? '#28a745' : '#dc3545'
    } else if (feedbackElement) {
      feedbackElement.textContent = ''
      feedbackElement.style.color = '#6c757d'
    }
  }

  /**
   * Resets validation UI to initial state
   */
  resetValidation() {
    if (!this.hasChecklistContainerTarget) return

    const container = this.checklistContainerTarget
    const rules = container.querySelectorAll('[data-rule]')
    
    rules.forEach(rule => {
      const icon = rule.querySelector('.validation-icon')
      const text = rule.querySelector('.validation-text')
      icon.innerHTML = '&nbsp;'
      icon.style.color = ''
      rule.style.color = ''
      
      // Reset text to base text (remove any messages)
      const baseText = text.textContent.split(' - ')[0]
      text.textContent = baseText
    })

    const feedbackElement = container.querySelector('[data-feedback]')
    if (feedbackElement) {
      feedbackElement.textContent = ''
    }

    this.updateSubmitButton(false)
  }

  /**
   * Enables or disables the submit button based on validation
   */
  updateSubmitButton(isValid) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !isValid
    
    if (isValid) {
      this.submitButtonTarget.classList.remove('btn-secondary')
      this.submitButtonTarget.classList.add('btn-primary')
    } else {
      this.submitButtonTarget.classList.remove('btn-primary')
      this.submitButtonTarget.classList.add('btn-secondary')
    }
  }

  /**
   * Event handler for password field input
   */
  onPasswordInput() {
    this.validatePassword()
  }

  /**
   * Event handler for email field change (to update weak words)
   */
  onEmailChange() {
    if (this.hasPasswordFieldTarget && this.passwordFieldTarget.value) {
      this.validatePassword()
    }
  }
}
